import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  RunYearEndCloseResult,
  YearEndClosePreview,
  YearEndClosing,
} from '@accounting/shared';
import { api } from '../lib/api';

export function useYearEndPreview(companyId: string | null, fiscalYear: number, asOfDate: string) {
  return useQuery({
    queryKey: ['year-end-preview', companyId ?? 'none', fiscalYear, asOfDate],
    enabled: companyId !== null && /^\d{4}-\d{2}-\d{2}$/.test(asOfDate),
    queryFn: () =>
      api.get<YearEndClosePreview>(`/companies/${companyId}/year-end/preview`, {
        fiscalYear: String(fiscalYear),
        asOfDate,
      }),
  });
}

export function useYearEndClosings(companyId: string | null) {
  return useQuery({
    queryKey: ['year-end-closings', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () =>
      api.get<{ closings: YearEndClosing[] }>(`/companies/${companyId}/year-end/closings`).then((r) => r.closings),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, companyId: string | null) {
  const c = companyId ?? 'none';
  qc.invalidateQueries({ queryKey: ['year-end-preview', c] });
  qc.invalidateQueries({ queryKey: ['year-end-closings', c] });
  qc.invalidateQueries({ queryKey: ['journal-entries', c] });
  qc.invalidateQueries({ queryKey: ['accounts-balances', c] });
  qc.invalidateQueries({ queryKey: ['report'] });
}

export function useRunYearEndClose(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fiscalYear, asOfDate }: { fiscalYear: number; asOfDate: string }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<RunYearEndCloseResult>(`/companies/${companyId}/year-end/close`, { fiscalYear, asOfDate });
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useReverseYearEndClose(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ closing: YearEndClosing }>(`/companies/${companyId}/year-end/closings/${id}/reverse`, { reason });
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}
